import { MapPin } from '@phosphor-icons/react/dist/ssr/MapPin';
import DetailSectionCard from './DetailSectionCard';
import { WorkSection } from './WorkSection';
import { AboutMeSection } from './AboutMeSection';
import { getSharedHometown } from '../../../utils/connectionName';

/**
 * AboutSection — the ONE grey card on the connection detail pages holding
 * everything the peer shared about themselves: where they live, their role and
 * company, what they're up to, and places they like.
 *
 * Hometown used to sit in its own card above this one (HometownSection, now
 * deleted). Combined into a single container on the user's call, 2026-08-20 —
 * two stacked grey cards read as two unrelated facts when they are one picture
 * of a person.
 *
 * This is also now the SINGLE rendering used by both detail pages. The Anon
 * page previously carried its own inline copy of this card — the copy where
 * role and company were joined with " @ " — so the two drifted apart in exactly
 * the way the plan flagged. One component, no drift.
 *
 * Consent is unchanged and still gated per field: hometown by
 * getSharedHometown, the rest by the aboutMe bundle (hasAboutContent). The card
 * renders when EITHER is present, and each part appears only if shared — the
 * four combinations in the 2026-08-20 frames fall out of that.
 *
 * Props:
 *   userData - the peer's shared data (profileData / anonData)
 */

/**
 * The ABOUT consent gate, shared with surfaces that render the about content
 * in their own card language — one predicate, no drift.
 */
export const hasAboutContent = (userData) =>
  !!(
    userData?.sharedFields?.includes('aboutMe') &&
    (userData.role || userData.organization || userData.whatIDo || userData.placesLived)
  );

const AboutSection = ({ userData }) => {
  const hometown = getSharedHometown(userData);
  const hasAbout = hasAboutContent(userData);
  if (!hometown && !hasAbout) return null;

  return (
    // "Lives in" labels the card only when there IS a hometown; with work alone
    // the role line names it, as it always has.
    <DetailSectionCard label={hometown ? 'Lives in' : undefined}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {hometown && (
          <div
            style={{
              fontSize: '16px',
              fontWeight: '400',
              color: '#000000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            <MapPin size={16} color="#888888" weight="regular" style={{ flexShrink: 0 }} />
            {hometown}
          </div>
        )}
        <WorkSection userData={userData} />
        <AboutMeSection userData={userData} />
      </div>
    </DetailSectionCard>
  );
};

export default AboutSection;
